import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDialog } from '../../context/DialogContext';
import { Panel, PanelHeader, SurfaceButton, MUTED, SUBTLE, DANGER } from '../surface';
import LoadingSpinner from '../LoadingSpinner';
import { BRAND_URL } from '../../utils/brand';
import { composeSms, smsSegments, SMS_MAX_BODY, SMS_STOP_LINE } from '../../utils/smsText';
import SendPanel from './SendPanel';
import AudiencePicker, { emptyAudience } from './AudiencePicker';
import { Field, inputClass, inputStyle, toLocalInputValue } from './ui';

/**
 * The SMS builder — a page's text to its followers, picked people or contact list. One
 * textarea with a hard cap, a live segment count, a LINK inserter (your page, an event, a
 * custom URL) and a phone-shaped preview of the exact message: the page name in front, the
 * STOP line after, both counted. The same `client` / `perms` contract as EmailBuilder.
 */
export default function SmsBuilder({ id, client, perms, page, onSavedNew, onState, testTarget = 'your phone', stacked = false }) {
  const { showAlert, showConfirm } = useDialog();
  const { canEdit, canCreate } = perms;
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState({ kind: 'followers', audience: emptyAudience(), picked: [] });
  const [reach, setReach] = useState(null);
  const [status, setStatus] = useState('Draft');
  const [problems, setProblems] = useState([]);
  const [serverCanSend, setServerCanSend] = useState(false);
  const [testSentAt, setTestSentAt] = useState(null);
  const [scheduledFor, setScheduledFor] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleInput, setScheduleInput] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const areaRef = useRef(null);

  const isLocked = status === 'Sending' || status === 'Sent' || status === 'Failed';
  const isScheduled = status === 'Scheduled';
  const readOnly = !canEdit || isLocked;

  useEffect(() => { onState?.({ status, subject: body.slice(0, 40), dirty, updatedAt, loading }); }, [onState, status, body, dirty, updatedAt, loading]);

  const loadOne = useCallback(async (targetId) => {
    const r = await client.get(targetId);
    if (!r?.success) return;
    const n = r.data.newsletter;
    setBody(n.smsBody || '');
    if (n.audienceKind) {
      setAudience((prev) => ({
        kind: n.audienceKind,
        audience: { userIds: n.audience?.userIds || [], contactIds: n.audience?.contactIds || [], groupIds: n.audience?.groupIds || [] },
        picked: prev.picked.filter((p) => (p.kind === 'user' ? n.audience?.userIds : n.audience?.contactIds)?.includes(p.id)),
      }));
    }
    setStatus(n.status);
    setTestSentAt(n.testSentAt);
    setScheduledFor(n.scheduledFor || null);
    setScheduleError(n.scheduleError || null);
    setScheduleInput(n.scheduledFor ? toLocalInputValue(n.scheduledFor) : '');
    setUpdatedAt(n.updatedAt || n.createdAt || null);
    setProblems(r.data.problems || []);
    setServerCanSend(!!r.data.canSend);
    if (r.data.reach) setReach(r.data.reach);
    setDirty(false);
  }, [client]);

  useEffect(() => {
    (async () => { setLoading(true); if (!isNew) await loadOne(id); setLoading(false); })();
  }, [id, isNew, loadOne]);

  const touch = () => setDirty(true);
  const composed = useMemo(() => composeSms(page?.name, body), [page?.name, body]);
  const segments = smsSegments(composed);
  const over = body.length > SMS_MAX_BODY;

  /** Drops text at the caret (or the end), with a space either side when needed. */
  const insertAtCaret = (text) => {
    const el = areaRef.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const before = body.slice(0, start);
    const after = body.slice(end);
    const lead = before && !/\s$/.test(before) ? ' ' : '';
    const tail = after && !/^\s/.test(after) ? ' ' : '';
    const next = `${before}${lead}${text}${tail}${after}`;
    setBody(next.slice(0, SMS_MAX_BODY + 200));
    touch();
    setLinkOpen(false);
    setCustomUrl('');
    requestAnimationFrame(() => { if (el) { el.focus(); const pos = (before + lead + text).length; el.setSelectionRange(pos, pos); } });
  };
  const pageUrl = `${BRAND_URL}${page?.publicPath || ''}`;
  const insertCustom = () => {
    let u = customUrl.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
    insertAtCaret(u);
  };

  const save = async () => {
    setSaving(true);
    const r = await client.save({ id: isNew ? undefined : id, channel: 'sms', audienceKind: audience.kind, audience: audience.audience, smsBody: body });
    setSaving(false);
    if (!r?.success) { await showAlert({ title: 'COULD NOT SAVE', message: r?.message || r?.error || 'Try again.', type: 'error' }); return null; }
    if (isNew) onSavedNew?.(r.data.id); else await loadOne(id);
    return r.data.id;
  };
  const sendTest = async () => {
    const savedId = await save();
    const target = savedId || id;
    if (!target) return;
    const r = await client.sendTest(target);
    if (r?.success) {
      if (!isNew) await loadOne(target);
      await showAlert({ title: 'TEST SENT', message: `Check ${testTarget}. That is exactly what everyone gets.`, type: 'success' });
    } else {
      await showAlert({ title: 'TEST FAILED', message: r?.message || r?.error || 'Try again.', type: 'error' });
    }
  };
  const doSchedule = async () => {
    if (!scheduleInput) return;
    const savedId = await save();
    const target = savedId || id;
    if (!target) return;
    const iso = new Date(scheduleInput).toISOString();
    const r = await client.schedule(target, iso);
    if (r?.success) { await loadOne(target); await showAlert({ title: 'SCHEDULED', message: `It will go out on ${new Date(iso).toLocaleString()}. Any edit needs a fresh test before it fires.`, type: 'success' }); }
    else await showAlert({ title: 'COULD NOT SCHEDULE', message: r?.message || r?.error || 'Try again.', type: 'error' });
  };
  const doUnschedule = async () => {
    const r = await client.unschedule(id);
    if (r?.success) await loadOne(id); else await showAlert({ title: 'COULD NOT UNSCHEDULE', message: r?.message || r?.error || 'Try again.', type: 'error' });
  };
  const doDuplicate = async () => {
    const r = await client.duplicate(id);
    if (r?.success) onSavedNew?.(r.data.id); else await showAlert({ title: 'COULD NOT DUPLICATE', message: r?.message || r?.error || 'Try again.', type: 'error' });
  };
  const doSend = async () => {
    const count = reach?.count ?? 0;
    const ok = await showConfirm({
      title: 'SEND THIS TEXT',
      message: `This texts ${count.toLocaleString()} ${count === 1 ? 'person' : 'people'} — ${segments} segment${segments === 1 ? '' : 's'} each. A text cannot be recalled.`,
      confirmLabel: `SEND TO ${count.toLocaleString()} ${count === 1 ? 'PERSON' : 'PEOPLE'}`, cancelLabel: 'CANCEL', variant: 'primary',
    });
    if (!ok) return;
    const r = await client.send(id);
    if (r?.success) { await loadOne(id); await showAlert({ title: 'ON ITS WAY', message: `Queued for ${r.data.queued} ${r.data.queued === 1 ? 'number' : 'numbers'}. Delivery runs in the background.`, type: 'success' }); }
    else await showAlert({ title: 'COULD NOT SEND', message: r?.message || r?.error || 'Try again.', type: 'error' });
  };

  if (loading) return <div className="p-6"><LoadingSpinner /></div>;

  const left = (
    <div className="flex flex-col" style={{ gap: 16 }}>
      <AudiencePicker ownerType={page.ownerType} ownerId={page.ownerId} channel="sms" followerWord={page.followerWord} value={audience}
        onChange={(v) => { setAudience(v); touch(); }} readOnly={readOnly} onReach={setReach} />
      <Panel data-testid="sms-compose">
        <PanelHeader label="THE TEXT" right={<span className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: over ? DANGER : SUBTLE }} data-testid="sms-count">{body.length}/{SMS_MAX_BODY} · {segments} SEGMENT{segments === 1 ? '' : 'S'}</span>} />
        <div className="flex flex-col" style={{ padding: 16, gap: 12 }}>
          <Field hint={`Your page name goes in front and "${SMS_STOP_LINE}" after — both count. Keep it to one segment (160 characters all in) and it lands as one bubble.`}>
            {readOnly ? (
              <div className="min-h-[96px] border-2 border-black bg-[#F5F5F5] px-3 py-[9px] font-mono text-[13px] whitespace-pre-wrap">{body || '—'}</div>
            ) : (
              <textarea ref={areaRef} rows={5} value={body} onChange={(e) => { setBody(e.target.value); touch(); }} maxLength={SMS_MAX_BODY + 200}
                placeholder="Doors at 8 Thursday. Tickets are half price for you until midnight." aria-label="Text message" className={`${inputClass} resize-none`} style={{ ...inputStyle, fontSize: 14, lineHeight: 1.5 }} data-testid="sms-body" />
            )}
          </Field>
          {over && <span className="font-mono" style={{ fontSize: 11, color: DANGER }}>Over by {body.length - SMS_MAX_BODY}. Cut it down — nothing saves past the cap.</span>}
          {!readOnly && (
            <div className="flex flex-col" style={{ gap: 8 }}>
              <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
                <SurfaceButton size="xs" onClick={() => insertAtCaret(pageUrl)} data-testid="insert-page-link">+ LINK TO YOUR PAGE</SurfaceButton>
                <SurfaceButton size="xs" onClick={() => setLinkOpen((o) => !o)} aria-expanded={linkOpen} data-testid="insert-link">+ LINK…</SurfaceButton>
              </div>
              {linkOpen && (
                <div className="flex flex-wrap items-center border-2 border-black bg-[#F5F5F5]" style={{ gap: 6, padding: 8 }} data-testid="link-inserter">
                  <input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); insertCustom(); } }}
                    placeholder="heyportal.app/e/kelowna/thursday-doors" aria-label="Link URL" className={inputClass} style={{ ...inputStyle, flex: '1 1 220px', width: 'auto' }} />
                  <SurfaceButton size="xs" variant="fill" onClick={insertCustom} disabled={!customUrl.trim()}>INSERT</SurfaceButton>
                </div>
              )}
              <span className="font-mono" style={{ fontSize: 10, color: MUTED }}>Links go in as the full address — carriers don't render anything shorter.</span>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );

  const right = (
    <div className="flex flex-col" style={{ gap: 16 }}>
      <SendPanel
        channel="sms" problems={problems} canSendNow={serverCanSend && !dirty && !over} dirty={dirty} saving={saving}
        isLocked={isLocked} isScheduled={isScheduled} status={status} testSentAt={testSentAt} scheduledFor={scheduledFor}
        scheduleError={scheduleError} scheduleInput={scheduleInput} onScheduleInput={setScheduleInput}
        reach={reach} budgetLine={`${segments} SEGMENT${segments === 1 ? '' : 'S'} PER PERSON`}
        perms={perms} isNew={isNew} noun="PEOPLE" testTarget={testTarget}
        onSave={save} onTest={sendTest} onSend={doSend} onSchedule={doSchedule} onUnschedule={doUnschedule} onDuplicate={doDuplicate}
      />
      <SmsPreview composed={composed} segments={segments} />
      {canCreate === false && null}
    </div>
  );

  if (stacked) return <div className="flex flex-col" style={{ gap: 16 }}>{left}{right}</div>;
  return (
    <div className="grid flex-1 items-start grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px]" style={{ gap: 16 }} data-testid="sms-builder">
      {left}
      {right}
    </div>
  );
}

/** The exact composed message, in a phone bubble on black — what the person actually reads. */
export function SmsPreview({ composed, segments }) {
  return (
    <div className="border-2 border-black bg-black p-5" data-testid="sms-preview">
      <div className="mb-3 flex items-baseline justify-between font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-[#9CA3AF]">
        <span>PREVIEW</span><span>{composed.length} CHARS · {segments} SEGMENT{segments === 1 ? '' : 'S'}</span>
      </div>
      <div className="mx-auto" style={{ maxWidth: 300 }}>
        <div className="font-mono text-center" style={{ fontSize: 9, letterSpacing: '0.16em', color: '#6B7280', marginBottom: 10 }}>PORTAL · SMS</div>
        <div style={{ background: '#E5E7EB', color: '#000', borderRadius: 18, borderTopLeftRadius: 4, padding: '10px 14px', fontFamily: '-apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif', fontSize: 15, lineHeight: 1.35, wordBreak: 'break-word' }}>
          {composed}
        </div>
      </div>
    </div>
  );
}
